/**
 * CatalogCard — web port of `apps/WunderGuide/components/catalog/CatalogCard.tsx`.
 *
 * Routes to a layout-specific renderer based on item.display.layout (or a
 * forced layout from the explore view-mode picker). 1:1 port of the RN
 * shared sub-components (PriceDisplay, RatingDisplay, DurationDisplay,
 * LocationDisplay, ProviderAttribution, HighlightBullet, FeatureIcons).
 */
import React, { useEffect, useRef, useState } from 'react';
import type { CatalogItemSummary } from '@wg/types';
import './CatalogCard.css';

export type CatalogLayout =
  | 'compact'
  | 'standard'
  | 'featured'
  | 'hero'
  | 'spotlight';

interface CatalogCardProps {
  item: CatalogItemSummary;
  onPress?: (item: CatalogItemSummary) => void;
  forceLayout?: CatalogLayout;
}

const CatalogCard: React.FC<CatalogCardProps> = ({
  item,
  onPress,
  forceLayout,
}) => {
  const layout = (forceLayout ||
    item.display?.layout ||
    'standard') as CatalogLayout;
  switch (layout) {
    case 'compact':
      return <CompactCard item={item} onPress={onPress} />;
    case 'hero':
    case 'featured':
    case 'spotlight':
      return <HeroCard item={item} onPress={onPress} />;
    case 'standard':
    default:
      return <StandardCard item={item} onPress={onPress} />;
  }
};

export default CatalogCard;

/* ── Standard ─────────────────────────────────────────────────────── */

const StandardCard: React.FC<{
  item: CatalogItemSummary;
  onPress?: (item: CatalogItemSummary) => void;
}> = ({ item, onPress }) => {
  const heightMul = item.display?.height_multiplier ?? 1.0;
  return (
    <button
      type="button"
      className="wg-cat wg-cat--standard"
      onClick={() => onPress?.(item)}
    >
      <div
        className="wg-cat__media"
        style={{ aspectRatio: `4 / ${3 * heightMul}` }}
      >
        {item.image?.url ? (
          <img src={item.image.url} alt={item.title} loading="lazy" />
        ) : (
          <div className="wg-cat__placeholder" />
        )}
        {item.display?.badge && (
          <Badge text={item.display.badge} color={item.display.badge_color} />
        )}
      </div>
      <div className="wg-cat__body">
        <div className="wg-cat__title">{item.title}</div>
        <LocationDisplay item={item} size="small" />
        <div className="wg-cat__meta-row">
          <RatingDisplay item={item} size="small" />
          <DurationDisplay item={item} size="small" />
        </div>
        <div className="wg-cat__bottom-row">
          <PriceDisplay item={item} size="medium" />
          <FeatureIcons item={item} />
        </div>
        <ProviderAttribution item={item} />
      </div>
    </button>
  );
};

/* ── Compact ──────────────────────────────────────────────────────── */

const CompactCard: React.FC<{
  item: CatalogItemSummary;
  onPress?: (item: CatalogItemSummary) => void;
}> = ({ item, onPress }) => (
  <button
    type="button"
    className="wg-cat wg-cat--compact"
    onClick={() => onPress?.(item)}
  >
    <div className="wg-cat__compact-media">
      {item.image?.url ? (
        <img src={item.image.url} alt={item.title} loading="lazy" />
      ) : (
        <div className="wg-cat__placeholder" />
      )}
    </div>
    <div className="wg-cat__compact-body">
      <div className="wg-cat__title">{item.title}</div>
      <LocationDisplay item={item} size="small" />
      <div className="wg-cat__bottom-row">
        <RatingDisplay item={item} size="small" showCount={false} />
        <PriceDisplay item={item} size="small" />
      </div>
    </div>
  </button>
);

/* ── Hero ─────────────────────────────────────────────────────────── */

const HeroCard: React.FC<{
  item: CatalogItemSummary;
  onPress?: (item: CatalogItemSummary) => void;
}> = ({ item, onPress }) => {
  const heightMul = item.display?.height_multiplier ?? 1.5;
  const slideshow = item.display?.slideshow_images ?? [];
  const allImages = item.image?.url
    ? [item.image.url, ...slideshow]
    : slideshow;
  const [current, setCurrent] = useState(0);
  const stripRef = useRef<HTMLDivElement | null>(null);

  // Auto-advance slideshow.
  useEffect(() => {
    if (allImages.length <= 1) return;
    const id = window.setInterval(() => {
      setCurrent(i => {
        const n = (i + 1) % allImages.length;
        const el = stripRef.current;
        if (el) {
          el.scrollTo({ left: n * el.clientWidth, behavior: 'smooth' });
        }
        return n;
      });
    }, 4000);
    return () => window.clearInterval(id);
  }, [allImages.length]);

  const onScroll = () => {
    const el = stripRef.current;
    if (!el) return;
    setCurrent(Math.round(el.scrollLeft / el.clientWidth));
  };

  const highlights = item.display?.highlights ?? [];

  return (
    <button
      type="button"
      className="wg-cat wg-cat--hero"
      onClick={() => onPress?.(item)}
    >
      <div
        className="wg-cat__hero-media"
        style={{ aspectRatio: `4 / ${3 * heightMul}` }}
      >
        {allImages.length > 1 ? (
          <div
            ref={stripRef}
            className="wg-cat__hero-strip"
            onScroll={onScroll}
          >
            {allImages.map((url, i) => (
              <img key={i} src={url} alt="" loading="lazy" />
            ))}
          </div>
        ) : item.image?.url ? (
          <img src={item.image.url} alt={item.title} loading="lazy" />
        ) : (
          <div className="wg-cat__placeholder" />
        )}

        {item.display?.badge && (
          <Badge text={item.display.badge} color={item.display.badge_color} />
        )}

        {allImages.length > 1 && (
          <div className="wg-cat__hero-dots">
            {allImages.map((_, i) => (
              <span
                key={i}
                className={`wg-cat__hero-dot${i === current ? ' is-active' : ''}`}
              />
            ))}
          </div>
        )}

        <div className="wg-cat__hero-overlay">
          {item.display?.tagline && (
            <div className="wg-cat__hero-tagline">{item.display.tagline}</div>
          )}
          <div className="wg-cat__hero-title">{item.title}</div>
        </div>
      </div>

      <div className="wg-cat__body">
        <div className="wg-cat__meta-row">
          <RatingDisplay item={item} size="large" />
          <DurationDisplay item={item} size="medium" />
          <FeatureIcons item={item} />
        </div>
        <LocationDisplay item={item} size="medium" />
        {item.short_description && item.display?.show_description && (
          <p className="wg-cat__hero-desc">{item.short_description}</p>
        )}
        {highlights.length > 0 && (
          <div className="wg-cat__highlights">
            {highlights.slice(0, 4).map((h, i) => (
              <HighlightBullet key={i} text={h} />
            ))}
          </div>
        )}
        <div className="wg-cat__bottom-row">
          <PriceDisplay item={item} size="large" />
          <FeatureIcons item={item} />
        </div>
        <ProviderAttribution item={item} />
      </div>
    </button>
  );
};

/* ── Shared sub-components ────────────────────────────────────────── */

type Size = 'small' | 'medium' | 'large';

export const PriceDisplay: React.FC<{
  item: CatalogItemSummary;
  size?: Size;
}> = ({ item, size = 'medium' }) => {
  const fontSize = size === 'small' ? 12 : size === 'large' ? 18 : 14;
  if (!item.price && item.price_display) {
    return (
      <span className="wg-cat__price-row">
        <span className="wg-cat__price" style={{ fontSize }}>
          {item.price_display}
        </span>
      </span>
    );
  }
  if (!item.price) return null;
  const currency = item.price.currency || 'USD';
  const fmt = (amt: number) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: amt % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amt);
    } catch {
      return `${currency} ${amt.toFixed(2)}`;
    }
  };
  const isPerPerson = item.price.per === 'person';
  return (
    <span className="wg-cat__price-row">
      {item.price.original_amount && (
        <span
          className="wg-cat__price-original"
          style={{ fontSize: fontSize - 2 }}
        >
          {fmt(item.price.original_amount)}
        </span>
      )}
      <span className="wg-cat__price" style={{ fontSize }}>
        {fmt(item.price.amount)}
      </span>
      {isPerPerson ? (
        <span className="wg-cat__price-per">
          /<PersonIcon />
        </span>
      ) : (
        <span className="wg-cat__price-per" style={{ fontSize: fontSize - 4 }}>
          /{item.price.per}
        </span>
      )}
    </span>
  );
};

export const RatingDisplay: React.FC<{
  item: CatalogItemSummary;
  size?: Size;
  showCount?: boolean;
}> = ({ item, size = 'medium', showCount = true }) => {
  if (!item.reviews?.rating) return null;
  const fontSize = size === 'small' ? 11 : size === 'large' ? 16 : 13;
  return (
    <span className="wg-cat__rating">
      <StarIcon />
      <span style={{ fontSize }}>{item.reviews.rating.toFixed(1)}</span>
      {showCount && item.reviews.count > 0 && (
        <span
          className="wg-cat__rating-count"
          style={{ fontSize: fontSize - 2 }}
        >
          ({item.reviews.count.toLocaleString()})
        </span>
      )}
    </span>
  );
};

export const DurationDisplay: React.FC<{
  item: CatalogItemSummary;
  size?: Size;
}> = ({ item, size = 'medium' }) => {
  const text =
    item.duration_text ||
    (item.duration_minutes
      ? `${Math.floor(item.duration_minutes / 60)}h ${item.duration_minutes % 60}m`
      : null);
  if (!text) return null;
  const fontSize = size === 'small' ? 11 : size === 'large' ? 14 : 12;
  return (
    <span className="wg-cat__duration">
      <ClockIcon />
      <span style={{ fontSize }}>{text}</span>
    </span>
  );
};

export const LocationDisplay: React.FC<{
  item: CatalogItemSummary;
  size?: Size;
}> = ({ item, size = 'small' }) => {
  const loc = item.location;
  if (!loc) return null;
  const isUS =
    loc.country === 'US' ||
    loc.country === 'USA' ||
    loc.country === 'United States';
  const parts: string[] = [];
  if (loc.neighborhood) parts.push(loc.neighborhood);
  if (loc.city && loc.city !== loc.neighborhood) parts.push(loc.city);
  if (loc.island) parts.push(loc.island);
  else if (loc.state && isUS) parts.push(loc.state);
  if (loc.country && !isUS && !loc.island) parts.push(loc.country);
  if (parts.length === 0) return null;
  const fontSize = size === 'small' ? 11 : size === 'large' ? 14 : 12;
  return (
    <span className="wg-cat__loc" style={{ fontSize }}>
      <PinIcon />
      <span>{parts.join(', ')}</span>
    </span>
  );
};

export const ProviderAttribution: React.FC<{ item: CatalogItemSummary }> = ({
  item,
}) => {
  if (!item.provider_name) return null;
  return <span className="wg-cat__provider">via {item.provider_name}</span>;
};

export const HighlightBullet: React.FC<{ text: string }> = ({ text }) => (
  <span className="wg-cat__highlight">
    <CheckIcon />
    <span>{text}</span>
  </span>
);

export const FeatureIcons: React.FC<{ item: CatalogItemSummary }> = ({
  item,
}) => {
  const flags = [
    item.instant_confirmation && { key: 'instant', glyph: <BoltIcon /> },
    item.free_cancellation && { key: 'cancel', glyph: <NoEntryIcon /> },
    item.mobile_ticket && { key: 'ticket', glyph: <PhoneIcon /> },
  ].filter(Boolean) as { key: string; glyph: React.ReactNode }[];
  if (flags.length === 0) return null;
  return (
    <span className="wg-cat__features">
      {flags.map(f => (
        <span key={f.key} className="wg-cat__feature-icon">
          {f.glyph}
        </span>
      ))}
    </span>
  );
};

const Badge: React.FC<{ text: string; color?: string }> = ({ text, color }) => (
  <span
    className="wg-cat__badge"
    style={color ? { backgroundColor: color } : undefined}
  >
    {text}
  </span>
);

/* ── Glyphs ──────────────────────────────────────────────────────── */

function StarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="#FBBF24" aria-hidden>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 22s7-7.58 7-13a7 7 0 1 0-14 0c0 5.42 7 13 7 13z" />
      <circle cx="12" cy="9" r="2.6" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <polyline points="9 12 11.5 14.5 16 10" />
    </svg>
  );
}
function BoltIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}
function NoEntryIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function PhoneIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="6" y="2" width="12" height="20" rx="2.5" />
      <line x1="11" y1="18" x2="13" y2="18" />
    </svg>
  );
}
function PersonIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 22c1.4-4 4.4-6 8-6s6.6 2 8 6z" />
    </svg>
  );
}
